import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { colors, Container, Button, Card, Section } from '../styles/GlobalStyles';
import { RegistrationData, ParticipantMenu, MenuItem } from '../types';
import { backendApiService } from '../services/backendApiService';
import { configService } from '../services/configService';
import PaymentChooser from './PaymentChooser';

import PaymentSuccess from './PaymentSuccess';

const RegistrationSection = styled(Section)`
  background: linear-gradient(135deg, ${colors.cream} 0%, ${colors.white} 100%);
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 10px 0;
  }
`;

const BackButtonContainer = styled.div`
  max-width: 800px;
  margin: 0 auto 30px auto;
  width: 100%;
  
  @media (max-width: 768px) {
    margin: 0 10px 20px 10px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1.5px solid ${colors.secondary};
  border-radius: 25px;
  color: ${colors.brown};
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    background: ${colors.secondary};
    border-color: ${colors.primary};
    color: ${colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(139, 0, 0, 0.2);
    
    &:before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .back-icon {
    font-size: 1rem;
    transition: transform 0.3s ease;
  }
  
  &:hover .back-icon {
    transform: translateX(-3px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.85rem;
    border-radius: 20px;
    gap: 6px;
    
    &:hover {
      transform: none; /* Rimuove l'effetto hover su mobile */
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    
    &:hover .back-icon {
      transform: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 0.8rem;
  }
`;

const FormCard = styled(Card)`
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    margin: 0 10px;
    padding: 15px;
  }
`;

const FormTitle = styled.h1`
  text-align: center;
  color: ${colors.primary};
  margin-bottom: 40px;
  font-size: 2.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const TentNotice = styled(motion.div)`
  background: ${colors.cream};
  border: 1px solid ${colors.secondary};
  border-left: 4px solid ${colors.primary};
  padding: 18px 20px;
  margin-bottom: 30px;
  font-size: 1rem;
  line-height: 1.6;
  color: ${colors.brown};

  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 0.95rem;
    margin-bottom: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${colors.brown};
  font-size: 1.1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 1px solid ${colors.secondary};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: ${colors.white};
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(139, 0, 0, 0.1);
  }
  
  &:invalid {
    border-color: #dc3545;
  }
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px; /* Impedisce lo zoom su iOS */
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 15px;
  border: 1px solid ${colors.secondary};
  border-radius: 8px;
  font-size: 1rem;
  background: ${colors.white};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(139, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px; /* Impedisce lo zoom su iOS */
  }
`;

const ParticipantSection = styled.div`
  background: ${colors.cream};
  padding: 25px;
  border-radius: 12px;
  margin: 20px 0;
  
  
  @media (max-width: 768px) {
    padding: 15px;
    margin: 15px 0;
  }
`;

const ParticipantTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const RemoveParticipantButton = styled.button`
  background: none;
  border: none;
  color: ${colors.lightBrown};
  cursor: pointer;
  font-size: 1rem;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.primary};
    background: ${colors.white};
  }
`;

const AddParticipantButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  margin-top: 10px;
  background: transparent;
  border: 1px dashed ${colors.secondary};
  border-radius: 8px;
  color: ${colors.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.primary};
    background: ${colors.cream};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 15px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MenuOption = styled.div`
  background: ${colors.white};
  padding: 15px;
  border-radius: 8px;
  border: 1px solid ${colors.secondary};
  margin-bottom: 10px;
  
  h4 {
    color: ${colors.primary};
    margin-bottom: 5px;
  }
  
  p {
    color: ${colors.brown};
    font-size: 0.9rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  font-size: 1.3rem;
  padding: 20px;
  margin-top: 30px;
  background: ${colors.primary};
  color: ${colors.white};
  
  &:hover {
    background: ${colors.darkRed};
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    
    &:hover {
      background: #ccc;
      transform: none;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 15px;
    margin-top: 20px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    accent-color: ${colors.primary};
  }
  
  label {
    margin: 0;
    font-size: 1rem;
    cursor: pointer;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 5px;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SecondoPiattoSection = styled.div`
  margin-top: 15px;
  
  border-radius: 8px;
  
  h4 {
    color: ${colors.darkRed};
    margin: 0 0 10px 0;
    font-size: 1rem;
  }
`;

const SecondoPiattoSelect = styled(Select)`
  margin-top: 10px;
  
  &:invalid, &.error {
    border-color: #dc3545;
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
  }
`;

const SummarySection = styled.div`
  background: ${colors.grey};
  padding: 20px;
  border-radius: 12px;
  margin: 30px 0;
  border: 1px solid ${colors.primary};
  
  h3 {
    color: ${colors.brown};
    margin-bottom: 15px;
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    margin: 20px 0;
    
    p {
      font-size: 0.95rem;
      margin-bottom: 8px;
    }
  }
`;

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FinalHomeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px 35px;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.darkRed} 100%);
  border: none;
  border-radius: 35px;
  color: ${colors.white};
  font-size: 1.2rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.4s ease;
  box-shadow: 0 6px 20px rgba(139, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.7s;
  }
  
  &:hover {
    background: linear-gradient(135deg, ${colors.darkRed} 0%, ${colors.primary} 100%);
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 10px 30px rgba(139, 0, 0, 0.4);
    
    &:before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(139, 0, 0, 0.3);
  }
  
  .success-icon {
    font-size: 1.3rem;
    transition: transform 0.4s ease;
  }
  
  &:hover .success-icon {
    transform: scale(1.3) rotate(15deg);
  }
  
  @media (max-width: 768px) {
    padding: 15px 28px;
    font-size: 1.1rem;
    border-radius: 30px;
    gap: 10px;
    
    &:hover {
      transform: none; /* Rimuove l'effetto hover su mobile */
      box-shadow: 0 6px 20px rgba(139, 0, 0, 0.3);
    }
    
    &:hover .success-icon {
      transform: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 1rem;
    gap: 8px;
  }
`;

interface RegistrationPageProps {
  onBackClick: () => void;
  onShowClosureMessage: (message: string) => void;
}

const menuOptions: MenuItem[] = [
  { id: 'adulto', name: 'Menu Adulto', description: 'Antipasti: Speck, Cetriolini, Wurstel Bianco, Brezel • Secondi: Stinco con Rösti di patate o Wiener Schnitzel con Rösti di patate (da scegliere) • Dolci: Strudel con gelato' },
  { id: 'bambino', name: 'Menu Bambino', description: 'Pasta al sugo • Wiener Schnitzel con Rösti di patate• Strudel con gelato' },
  { id: 'vegetariano', name: 'Menu Vegetariano', description: 'Antipasti: Tortino salato, Cetriolini, Crauti crudi, Brezel • Secondi: Hamburger di verdure con Rösti di patate • Dolci: Strudel con gelato' }
];

const PREZZO_BICCHIERE = 5;

export const RegistrationPage: React.FC<RegistrationPageProps> = ({ onBackClick, onShowClosureMessage }) => {
  const [formData, setFormData] = useState<RegistrationData>({
    email: configService.isDevelopment() ? 'ariadifest@gmail.com' : '',
    nome: configService.isDevelopment() ? 'Mario' : '',
    cognome: configService.isDevelopment() ? 'Rossi' : '',
    telefono: configService.isDevelopment() ? '3331234567' : '',
    numeroPartecipanti: 1,
    menuPartecipanti: [{ menuType: 'adulto' }], // Nessun valore predefinito per secondoPiatto
    nomeGruppo: configService.isDevelopment() ? 'Gruppo Test' : '' // Campo facoltativo per raggruppamento tavoli
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState<string>('');
  
  // Stati per PayPal
  const [showPayPal, setShowPayPal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  
  // Stati per il flusso post-pagamento
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [isBackendProcessing, setIsBackendProcessing] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email è obbligatoria';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }

    if (!formData.nome) {
      newErrors.nome = 'Nome è obbligatorio';
    }

    if (!formData.cognome) {
      newErrors.cognome = 'Cognome è obbligatorio';
    }

    if (!formData.telefono) {
      newErrors.telefono = 'Numero di telefono è obbligatorio';
    } else if (!isMobilePhone(formData.telefono.trim(), 'any')) {
      // Stessa validazione (stessa libreria) usata dal backend: un numero che passa qui
      // non deve mai essere rifiutato dal server dopo un pagamento già effettuato.
      newErrors.telefono = 'Numero di telefono non valido';
    }

    // Validazione seconda scelta per menu adulto
    formData.menuPartecipanti.forEach((menu, index) => {
      if (menu.menuType === 'adulto' && !menu.secondoPiatto) {
        newErrors[`secondoPiatto_${index}`] = `Seleziona il secondo piatto per il partecipante ${index + 1}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Ricontrolla lo stato delle iscrizioni: l'utente potrebbe avere il form aperto da prima
    // che venissero chiuse, e nessun refresh della pagina glielo avrebbe segnalato nel frattempo.
    const status = await backendApiService.checkRegistrationStatus();
    if (!status.open) {
      onShowClosureMessage(status.message);
      return;
    }

    // Mostra il componente PayPal invece di inviare subito
    setShowPayPal(true);
  };

  // Ultima spiaggia: se un pagamento riuscito non si riesce a salvare da nessuna parte
  // (nemmeno tramite la segnalazione di emergenza al backend), teniamone almeno traccia
  // nel browser dell'utente, recuperabile in caso contatti l'assistenza.
  const persistFailedAttemptLocally = (registrationWithPayment: any, outcome: any) => {
    try {
      const key = 'ariadifest_failed_registrations';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push({
        clientTimestamp: new Date().toISOString(),
        formData: registrationWithPayment,
        outcome
      });
      localStorage.setItem(key, JSON.stringify(existing));
      console.log('💾 [REG-FLOW] Backup locale del tentativo fallito salvato in localStorage');
    } catch (storageError) {
      console.error('❌ [REG-FLOW] Impossibile salvare il backup locale:', storageError);
    }
  };

  const handlePaymentSuccess = (details: any) => {
    console.log('💳 [REG-FLOW] Pagamento riuscito', {
      provider: details?.paymentMethod,
      orderId: details?.orderId,
      amount: details?.amount,
      isFullPayment: details?.isFullPayment
    });

    setPaymentDetails(details);
    setPaymentCompleted(true);
    setShowPayPal(false);  // Nascondi PayPal
    setShowPaymentSuccess(true);  // Mostra successo immediato

    // Avvia il processo backend in background dopo un breve delay
    setTimeout(() => {
      submitRegistrationWithPayment(details);
    }, 2000);  // 2 secondi per leggere il messaggio di successo
  };

  const handlePaymentError = (error: string) => {
    console.error('❌ [REG-FLOW] Errore pagamento (nessun addebito):', error);
    setSubmitError(error);
    setShowPayPal(false);
  };

  const submitRegistrationWithPayment = async (paymentDetails: any) => {
    setIsBackendProcessing(true);
    setSubmitError('');

    const registrationWithPayment = {
      ...formData,
      paymentDetails: paymentDetails
    };

    console.log('🚀 [REG-FLOW] Avvio salvataggio registrazione dopo pagamento riuscito', {
      email: formData.email,
      orderId: paymentDetails?.orderId,
      amount: paymentDetails?.amount
    });

    try {
      const result = await backendApiService.submitRegistration(registrationWithPayment);

      if (result.success) {
        console.log('✅ [REG-FLOW] Registrazione salvata con successo', {
          bookingNumber: result.data?.bookingNumber,
          attemptId: result.attemptId
        });
        setSubmitSuccess(result.message || 'La tua registrazione è stata salvata con successo!');
        setShowPaymentSuccess(false); // Nascondi la schermata PaymentSuccess
        setSubmitted(true); // Mostra la schermata finale
      } else {
        console.error('❌ [REG-FLOW] Registrazione rifiutata dal backend DOPO un pagamento riuscito:', result);
        const errorMessage = result.errors && result.errors.length > 0
          ? result.errors.map(err => err.msg).join(', ')
          : result.message;

        setSubmitError(
          `Errore durante il salvataggio: ${errorMessage}` +
          (result.attemptId ? ` (riferimento: ${result.attemptId})` : '')
        );

        // Il pagamento è già stato incassato: non possiamo permetterci di perdere questo
        // tentativo solo perché il salvataggio "normale" è stato rifiutato.
        backendApiService.reportRegistrationIssue({
          formData: registrationWithPayment,
          backendResponse: result,
          clientTimestamp: new Date().toISOString()
        });
        persistFailedAttemptLocally(registrationWithPayment, result);
        // In caso di errore, mantieni showPaymentSuccess=true per mostrare l'errore
      }
    } catch (error) {
      console.error('❌ [REG-FLOW] Errore di connessione durante il salvataggio dopo pagamento riuscito:', error);
      setSubmitError('Errore di connessione. I tuoi dati potrebbero essere salvati comunque. Controlla la tua email per la conferma.');

      backendApiService.reportRegistrationIssue({
        formData: registrationWithPayment,
        error: error instanceof Error ? error.message : String(error),
        clientTimestamp: new Date().toISOString()
      });
      persistFailedAttemptLocally(registrationWithPayment, { error: error instanceof Error ? error.message : String(error) });
      // In caso di errore, mantieni showPaymentSuccess=true per mostrare l'errore
    } finally {
      setIsBackendProcessing(false);
    }
  };

  const handleBackFromPayPal = () => {
    setShowPayPal(false);
    setPaymentCompleted(false);
    setPaymentDetails(null);
    setSubmitError('');
    setShowPaymentSuccess(false);
    setIsBackendProcessing(false);
  };

  const handleInputChange = (field: keyof RegistrationData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Rimuovi errore quando l'utente inizia a digitare
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleMenuChange = (participantIndex: number, value: string) => {
    const newMenu = [...formData.menuPartecipanti];
    const newMenuType = value as 'adulto' | 'bambino' | 'vegetariano';
    
    newMenu[participantIndex] = {
      menuType: newMenuType,
      // Non impostiamo un valore predefinito per il secondo piatto
      secondoPiatto: newMenuType === 'adulto' ? undefined : undefined
    };
    setFormData(prev => ({ ...prev, menuPartecipanti: newMenu }));
  };

  const handleSecondoPiattoChange = (participantIndex: number, value: string) => {
    const newMenu = [...formData.menuPartecipanti];
    newMenu[participantIndex] = {
      ...newMenu[participantIndex],
      secondoPiatto: value ? (value as 'stinco' | 'cotoletta') : undefined
    };
    setFormData(prev => ({ ...prev, menuPartecipanti: newMenu }));
    
    // Rimuovi errore quando l'utente fa una selezione valida
    if (value && errors[`secondoPiatto_${participantIndex}`]) {
      setErrors(prev => ({ ...prev, [`secondoPiatto_${participantIndex}`]: '' }));
    }
  };

  const handleBicchiereChange = (participantIndex: number, checked: boolean) => {
    const newMenu = [...formData.menuPartecipanti];
    newMenu[participantIndex] = {
      ...newMenu[participantIndex],
      bicchiereCommemorativo: checked
    };
    setFormData(prev => ({ ...prev, menuPartecipanti: newMenu }));
  };

  const addParticipant = () => {
    if (formData.menuPartecipanti.length >= 20) return; // Limite massimo di 20 partecipanti

    const newMenu = [...formData.menuPartecipanti, { menuType: 'adulto' as const }];
    setFormData(prev => ({ ...prev, numeroPartecipanti: newMenu.length, menuPartecipanti: newMenu }));
  };

  const removeParticipant = (participantIndex: number) => {
    if (participantIndex === 0) return; // Il primo partecipante non è rimovibile

    const newMenu = formData.menuPartecipanti.filter((_, index) => index !== participantIndex);
    setFormData(prev => ({ ...prev, numeroPartecipanti: newMenu.length, menuPartecipanti: newMenu }));

    // Rimuovi eventuali errori associati al partecipante eliminato
    setErrors(prev => {
      const next = { ...prev };
      delete next[`secondoPiatto_${participantIndex}`];
      return next;
    });
  };

  // Mostra la pagina di successo del pagamento
  if (showPaymentSuccess) {
    return (
      <PaymentSuccess 
        paymentDetails={paymentDetails}
        formData={formData}
        isBackendProcessing={isBackendProcessing}
        onBackClick={onBackClick}
        submitError={submitError}
        submitSuccess={submitSuccess}
      />
    );
  }

  if (submitted) {
    return (
      <RegistrationSection>
        <Container>
          <FormCard>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ textAlign: 'center' }}
            >
              ✅
              <h1 style={{ color: colors.primary, marginBottom: '20px' }}>Registrazione Completata!</h1>
              {paymentDetails && (
                <div style={{ 
                  background: colors.secondary, 
                  padding: '20px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: `2px solid ${colors.primary}`
                }}>
                  <h3 style={{ color: colors.brown, marginBottom: '10px' }}>💳 Pagamento Confermato</h3>
                  <p style={{ color: colors.brown, marginBottom: '5px' }}>
                    <strong>ID Transazione:</strong> {paymentDetails.orderId}
                  </p>
                  <p style={{ color: colors.brown, marginBottom: '5px' }}>
                    <strong>Importo:</strong> €{paymentDetails.amount}
                  </p>
                  <p style={{ color: colors.brown }}>
                    <strong>Email PayPal:</strong> {paymentDetails.payerEmail}
                  </p>
                </div>
              )}
              {submitSuccess && (
                <p style={{ 
                  fontSize: '1.2rem', 
                  marginBottom: '30px', 
                  color: colors.forestGreen, 
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6'
                }}>
                  {submitSuccess}
                </p>
              )}
              {!submitSuccess && (
                <p style={{ fontSize: '1.2rem', marginBottom: '30px', color: colors.brown }}>
                  Grazie per la tua iscrizione a "C'è Aria di Fest". I tuoi dati sono stati registrati.
                </p>
              )}
              {submitError && (
                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '8px', 
                  padding: '15px', 
                  marginBottom: '20px',
                  color: '#856404'
                }}>
                  ⚠️ {submitError}
                </div>
              )}
              <FinalHomeButton onClick={onBackClick}>
                <span className="success-icon">🏠</span>
                Torna alla Home
              </FinalHomeButton>
            </motion.div>
          </FormCard>
        </Container>
      </RegistrationSection>
    );
  }

  // Mostra PayPal se il form è valido e l'utente ha cliccato "Conferma Registrazione"
  if (showPayPal) {
    const adults = formData.menuPartecipanti.filter(p => p.menuType === 'adulto' || p.menuType === 'vegetariano').length;
    const children = formData.menuPartecipanti.filter(p => p.menuType === 'bambino').length;
    const bicchieri = formData.menuPartecipanti.filter(p => p.bicchiereCommemorativo).length;

    return (
      <RegistrationSection>
        <Container>
          <BackButtonContainer>
            <BackButton onClick={handleBackFromPayPal}>
              <span className="back-icon">←</span>
              Indietro
            </BackButton>
          </BackButtonContainer>

          <FormCard>
            <FormTitle>💳 Pagamento Acconto</FormTitle>
            
            <SummarySection>
              <h3>Riepilogo Prenotazione</h3>
              <p><strong>Referente:</strong> {formData.nome} {formData.cognome}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Telefono:</strong> {formData.telefono}</p>
              {formData.nomeGruppo && <p><strong>Nome Gruppo:</strong> {formData.nomeGruppo}</p>}
              <p><strong>Numero Partecipanti:</strong> {formData.numeroPartecipanti}</p>
              <p><strong>Bambini:</strong> {children}</p>
              <p><strong>Adulti:</strong> {adults}</p>
              {bicchieri > 0 && (
                <p><strong>Bicchieri commemorativi:</strong> {bicchieri} x €{PREZZO_BICCHIERE} = €{bicchieri * PREZZO_BICCHIERE} (pagati per intero)</p>
              )}
            </SummarySection>

            {submitError && (
              <div style={{ 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '8px', 
                padding: '15px', 
                marginBottom: '20px',
                color: '#856404'
              }}>
                ⚠️ {submitError}
              </div>
            )}

            <PaymentChooser
              adults={adults}
              children={children}
              bicchieri={bicchieri}
              email={formData.email}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
            />
          </FormCard>
        </Container>
      </RegistrationSection>
    );
  }


  /*
  <SummarySection>
              
              <h3>Riepilogo Registrazione</h3>
              <p><strong>Referente:</strong> {formData.nome} {formData.cognome}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Telefono:</strong> {formData.telefono}</p>
              {formData.nomeGruppo && <p><strong>Nome Gruppo:</strong> {formData.nomeGruppo}</p>}
              <p><strong>Numero Partecipanti:</strong> {formData.numeroPartecipanti}</p>
              <p><strong>Menu Bambino:</strong> {formData.menuPartecipanti.filter(p => p.menuType === 'bambino').length}</p>
              <p><strong>Menu Adulto:</strong> {formData.menuPartecipanti.filter(p => p.menuType === 'adulto').length}</p>
              <p><strong>Menu Vegetariano:</strong> {formData.menuPartecipanti.filter(p => p.menuType === 'vegetariano').length}</p>
              
              
              {formData.menuPartecipanti.some(p => p.menuType === 'adulto') && (
                <div style={{ marginTop: '15px', padding: '10px', background: colors.cream, borderRadius: '5px' }}>
                  <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: colors.primary }}>Seconde Portate Menu Adulto:</p>
                  {formData.menuPartecipanti
                    .map((p, index) => ({ ...p, index }))
                    .filter(p => p.menuType === 'adulto')
                    .map(p => (
                      <p key={p.index} style={{ margin: '2px 0', fontSize: '0.9rem' }}>
                        Partecipante {p.index + 1}: {
                          p.secondoPiatto === 'stinco' ? '🥩 Stinco' : 
                          p.secondoPiatto === 'cotoletta' ? '🍖 Wiener Schnitzel' : 
                          <span style={{ color: '#dc3545', fontStyle: 'italic' }}>⚠️ Da selezionare</span>
                        }
                      </p>
                    ))
                  }
                </div>
              )}
            </SummarySection>
  */

  return (
    <RegistrationSection>
      <Container>
        <BackButtonContainer>
          <BackButton onClick={onBackClick}>
            <span className="back-icon">←</span>
            Torna alla Home
          </BackButton>
        </BackButtonContainer>

        <FormCard>
          <FormTitle>Registrazione Evento</FormTitle>

          <TentNotice
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            🎉 Vista la grande richiesta, abbiamo ampliato la festa con un tendone riscaldato direttamente collegato al salone: <b>le nuove iscrizioni troveranno posto lì</b> — stesso menù, stessa musica, stessa atmosfera. Un'unica festa, per tutti.<br/> Se ti unisci in un gruppo già iscritto sarai accorpato a loro
          </TentNotice>

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">
                Email *
              </Label>
              <Input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="la-tua-email@esempio.com"
                required
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </FormGroup>

            <ResponsiveGrid>
              <FormGroup>
                <Label htmlFor="nome">
                  Nome *
                </Label>
                <Input
                  type="text"
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder="Il tuo nome"
                  required
                />
                {errors.nome && <ErrorMessage>{errors.nome}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="cognome">
                  Cognome *
                </Label>
                <Input
                  type="text"
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => handleInputChange('cognome', e.target.value)}
                  placeholder="Il tuo cognome"
                  required
                />
                {errors.cognome && <ErrorMessage>{errors.cognome}</ErrorMessage>}
              </FormGroup>
            </ResponsiveGrid>

            <FormGroup>
              <Label htmlFor="telefono">
                Numero di Telefono *
              </Label>
              <Input
                type="tel"
                id="telefono"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                placeholder="+39 123 456 7890"
                required
              />
              {errors.telefono && <ErrorMessage>{errors.telefono}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="nomeGruppo">
                Nome Gruppo (facoltativo)
              </Label>
              <Input
                type="text"
                id="nomeGruppo"
                value={formData.nomeGruppo || ''}
                onChange={(e) => handleInputChange('nomeGruppo', e.target.value)}
                placeholder="Es: Famiglia Rossi, Amici del calcetto..."
              />
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
                💡 Se vuoi sederti con altre persone che si iscriveranno separatamente, inserite lo stesso nome gruppo
              </div>
            </FormGroup>

            {/* Menu per ogni partecipante */}
            {formData.menuPartecipanti.map((menu, index) => (
              <ParticipantSection key={index}>
                <ParticipantTitle>
                  <span>{menu.menuType === 'bambino' ? '👶' : '👤'} Partecipante {index + 1}</span>
                  {index > 0 && (
                    <RemoveParticipantButton
                      type="button"
                      onClick={() => removeParticipant(index)}
                      aria-label={`Rimuovi partecipante ${index + 1}`}
                    >
                      ✕ Rimuovi
                    </RemoveParticipantButton>
                  )}
                </ParticipantTitle>

                <div>
                  <Label>Tipo di Menu</Label>
                  <Select
                    value={menu.menuType}
                    onChange={(e) => handleMenuChange(index, e.target.value)}
                  >
                    {menuOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Select>

                   {/* Selezione secondo piatto per menu adulto */}
                  {menu.menuType === 'adulto' && (
                    <SecondoPiattoSection>
                      <h4>Scegli il secondo piatto *</h4>
                      <SecondoPiattoSelect
                        value={menu.secondoPiatto || ''}
                        onChange={(e) => handleSecondoPiattoChange(index, e.target.value)}
                        className={errors[`secondoPiatto_${index}`] ? 'error' : ''}
                      >
                        <option value="">-- Seleziona il secondo piatto --</option>
                        <option value="stinco">🍖 Stinco con Rösti di patate</option>
                        <option value="cotoletta">🥩 Wiener Schnitzel con Rösti di patate</option>
                      </SecondoPiattoSelect>
                      {errors[`secondoPiatto_${index}`] && (
                        <ErrorMessage>{errors[`secondoPiatto_${index}`]}</ErrorMessage>
                      )}
                    </SecondoPiattoSection>
                  )}
                  
                  <MenuOption style={{ marginTop: '10px' }}>
                    <h4>{menuOptions.find(opt => opt.id === menu.menuType)?.name}</h4>
                    <p>{menuOptions.find(opt => opt.id === menu.menuType)?.description}</p>
                  </MenuOption>

                  <CheckboxContainer style={{ marginTop: '10px' }}>
                    <input
                      type="checkbox"
                      id={`bicchiere_${index}`}
                      checked={!!menu.bicchiereCommemorativo}
                      onChange={(e) => handleBicchiereChange(index, e.target.checked)}
                    />
                    <label htmlFor={`bicchiere_${index}`}>
                      🍺 Aggiungi bicchiere commemorativo (+€{PREZZO_BICCHIERE})
                    </label>
                  </CheckboxContainer>
                </div>
              </ParticipantSection>
            ))}

            <AddParticipantButton
              type="button"
              onClick={addParticipant}
              disabled={formData.menuPartecipanti.length >= 20}
            >
              + Aggiungi partecipante
            </AddParticipantButton>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? '🔄 Salvando registrazione...' : 'Conferma Registrazione'}
            </SubmitButton>
          </form>
        </FormCard>
      </Container>
      

    </RegistrationSection>
  );
};
